/**
 * Single source of truth for text layout constants and conversions, shared
 * between the DOM/canvas2D renderer used by the Assembler (TextWidgetCanvas)
 * and the Pixi renderer used by the live broadcast (TextElement).
 *
 * Until this module existed, both renderers reimplemented the same maths
 * (px line-height, % → px letter-spacing, gradient vectors, decoration
 * offsets) with subtle drift — the symptom was author-positioned text in
 * the Assembler landing a few px off in the transmitted output.
 *
 * Rule: any constant or formula that affects glyph placement MUST live
 * here. Renderers consume; they don't compute.
 */

import type { IOverlayFill } from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

export const DEFAULT_FONT_FAMILY = "Roboto";
export const DEFAULT_FONT_SIZE = 24;
export const DEFAULT_LINE_HEIGHT_MULTIPLIER = 1.2;

/**
 * Padding subtracted from the destination width for word-wrap, and used as
 * the lateral offset for left/right-aligned text. Renderers must reserve
 * the same gutter on both sides or alignment will drift.
 */
export const LATERAL_PADDING = 20;
export const ALIGNMENT_OFFSET = 10;

export const UNDERLINE_OFFSET_RATIO = 0.45;
export const STRIKE_OFFSET_RATIO = 0;
export const DECORATION_THICKNESS_RATIO = 0.05;

export type TextAlign = "left" | "center" | "right";

export interface TextStyle {
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  fontGradient?: IOverlayFill;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  textAlign: TextAlign;
  lineHeightPx: number;
  letterSpacingPx: number;
  backgroundColor?: string;
}

/**
 * Author values arrive as `"150px"` strings (design-space px in the
 * 1920×1080 reference). Renderers scale via their own surface transform
 * — they don't multiply here. Earlier code applied a 2.01 fudge that
 * doubled scene-template headlines; that compensation is gone.
 */
export const parseFontSize = (sizeStr: string | undefined): number => {
  if (!sizeStr) return DEFAULT_FONT_SIZE;
  const size = parseInt(sizeStr, 10);
  if (Number.isNaN(size)) return DEFAULT_FONT_SIZE;
  return size;
};

export const computeLineHeightPx = (
  fontSize: number,
  multiplier: number | string | undefined,
): number => {
  if (multiplier === undefined || multiplier === null) {
    return fontSize * DEFAULT_LINE_HEIGHT_MULTIPLIER;
  }
  const mult =
    typeof multiplier === "number"
      ? multiplier
      : parseFloat(String(multiplier));
  if (Number.isNaN(mult)) return fontSize * DEFAULT_LINE_HEIGHT_MULTIPLIER;
  return fontSize * mult;
};

/**
 * `wordSpacing` is authored as a **percentage of font-size**. The DOM widget
 * historically interpreted it as `em` (which is font-size-relative, same
 * result for `letter-spacing`), so dividing by 100 and multiplying by
 * fontSize gives the canonical px value both renderers can use.
 */
export const computeLetterSpacingPx = (
  wordSpacingPct: number | undefined,
  fontSize: number,
): number => {
  if (!wordSpacingPct) return 0;
  return (wordSpacingPct / 100) * fontSize;
};

/**
 * Build the linear-gradient endpoints in **local texture space** (0..1
 * along each axis). The CSS angle convention (0deg → up, 90deg → right) is
 * preserved so the same `IOverlayFill.angle` paints identically in DOM
 * (background-image: linear-gradient), Pixi (FillGradient) and canvas2D
 * (createLinearGradient). The vector length covers the full bounding so
 * the rampa reaches edge-to-edge regardless of angle.
 */
export const buildLinearGradientLocalVectors = (
  angleDeg: number,
  width: number,
  height: number,
): { start: { x: number; y: number }; end: { x: number; y: number } } => {
  const angleRad = (angleDeg * Math.PI) / 180;
  const dx = Math.sin(angleRad);
  const dy = -Math.cos(angleRad);
  const lenU =
    Math.abs(Math.sin(angleRad)) +
    Math.abs(Math.cos(angleRad)) * (height / Math.max(1, width));
  const halfU = lenU / 2;
  return {
    start: { x: 0.5 - dx * halfU, y: 0.5 - dy * halfU },
    end: { x: 0.5 + dx * halfU, y: 0.5 + dy * halfU },
  };
};

/**
 * Convert an `IOverlayFill` into a canvas2D `CanvasGradient` (or a solid
 * color string). Coordinates are derived from `buildLinearGradientLocalVectors`
 * and scaled to the destination bounding so the gradient anchors to the
 * rendered glyph extents — same anchoring as Pixi's `textureSpace: "local"`.
 */
export const buildCanvas2DFill = (
  ctx: CanvasRenderingContext2D,
  fill: IOverlayFill | undefined,
  fallbackColor: string,
  width: number,
  height: number,
): string | CanvasGradient => {
  if (!fill || fill.kind === "solid") {
    return fill?.kind === "solid" ? fill.color : fallbackColor;
  }

  if (fill.kind === "linear") {
    const { start, end } = buildLinearGradientLocalVectors(
      fill.angle ?? 0,
      width,
      height,
    );
    const gradient = ctx.createLinearGradient(
      start.x * width,
      start.y * height,
      end.x * width,
      end.y * height,
    );
    fill.stops.forEach((s) => gradient.addColorStop(s.position / 100, s.color));
    return gradient;
  }

  const radius = Math.max(width, height) / 2;
  const gradient = ctx.createRadialGradient(
    width / 2,
    height / 2,
    0,
    width / 2,
    height / 2,
    radius,
  );
  fill.stops.forEach((s) => gradient.addColorStop(s.position / 100, s.color));
  return gradient;
};

/**
 * Resolve the raw `options` of a text element into a normalized `TextStyle`.
 * Both renderers call this so they agree on font-family fallback, line-height
 * units, letter-spacing units and default alignment before doing any layout.
 */
export const resolveTextStyle = (options: {
  fontFamily?: string;
  fontSize?: string;
  fontColor?: string;
  fontGradient?: IOverlayFill;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  textAlign?: TextAlign;
  lineHeight?: number;
  wordSpacing?: number;
  backgroundColor?: string;
}): TextStyle => {
  const fontSize = parseFontSize(options.fontSize);
  return {
    fontFamily: options.fontFamily ?? DEFAULT_FONT_FAMILY,
    fontSize,
    fontColor: options.fontColor ?? "#ffffff",
    fontGradient: options.fontGradient,
    bold: Boolean(options.bold),
    italic: Boolean(options.italic),
    underline: Boolean(options.underline),
    strike: Boolean(options.strike),
    textAlign: options.textAlign ?? "left",
    lineHeightPx: computeLineHeightPx(fontSize, options.lineHeight),
    letterSpacingPx: computeLetterSpacingPx(options.wordSpacing, fontSize),
    backgroundColor: options.backgroundColor,
  };
};

/**
 * Build a CSS font shorthand string (`bold italic 24px "Roboto"`) usable both
 * for `ctx.font` on canvas2D and for the `<textarea>` overlay of the editor.
 *
 * The `fontFamily` is wrapped in double quotes so families with spaces
 * (`Press Start 2P`, `DM Mono`, `Space Grotesk`) parse correctly. CSS
 * accepts unquoted multi-word families in stylesheets, but the canvas2D
 * font shorthand parser is stricter — without quotes it silently falls
 * back to the default sans-serif and the text renders ~10× smaller.
 * Already-quoted input is detected to avoid double-wrapping.
 */
export const buildCssFontString = (style: TextStyle): string => {
  const weight = style.bold ? "bold" : "normal";
  const italic = style.italic ? "italic" : "normal";
  const family = style.fontFamily.trim();
  const quoted =
    family.startsWith('"') || family.startsWith("'") ? family : `"${family}"`;
  return `${italic} ${weight} ${style.fontSize}px ${quoted}`;
};

/**
 * Anchor X for a given alignment, in the same convention Pixi uses
 * (0 = left, 0.5 = center, 1 = right). Renderers translate this into
 * their native API (Pixi `text.anchor.x`, canvas2D `ctx.textAlign`).
 */
export const anchorXForAlign = (align: TextAlign): number => {
  if (align === "center") return 0.5;
  if (align === "right") return 1;
  return 0;
};

/**
 * X position for the text origin within the bounding, applying the lateral
 * gutter. Mirrors the historical Pixi behavior:
 *   left  → x = ALIGNMENT_OFFSET
 *   center → x = width / 2
 *   right → x = width - ALIGNMENT_OFFSET
 */
export const originXForAlign = (align: TextAlign, width: number): number => {
  if (align === "center") return width / 2;
  if (align === "right") return width - ALIGNMENT_OFFSET;
  return ALIGNMENT_OFFSET;
};

export const wordWrapWidth = (width: number): number =>
  Math.max(0, width - LATERAL_PADDING);

export const decorationThickness = (fontSize: number): number =>
  Math.max(1, fontSize * DECORATION_THICKNESS_RATIO);

export const underlineOffsetPx = (fontSize: number): number =>
  fontSize * UNDERLINE_OFFSET_RATIO;

export interface FontMetrics {
  ascent: number;
  descent: number;
  fontSize: number;
}

/**
 * Same probe Pixi uses to derive font ascent/descent (`CanvasTextMetrics.measureFont`):
 * draw `|ÉqÅM` and read the actualBoundingBox extents. Cached per CSS font
 * string so repeated paints don't re-create canvases. Returning identical
 * numbers to Pixi is the precondition for the Assembler's canvas2D output
 * to line up vertically with the broadcast.
 */
const FONT_METRICS_PROBE = "|ÉqÅM";
const fontMetricsCache = new Map<string, FontMetrics>();
let probeCanvas: HTMLCanvasElement | null = null;
let probeCtx: CanvasRenderingContext2D | null = null;

export const measureFontMetrics = (font: string): FontMetrics => {
  const cached = fontMetricsCache.get(font);
  if (cached) return cached;

  if (typeof document === "undefined") {
    return { ascent: 0, descent: 0, fontSize: 0 };
  }
  if (!probeCanvas) {
    probeCanvas = document.createElement("canvas");
    probeCtx = probeCanvas.getContext("2d");
  }
  if (!probeCtx) return { ascent: 0, descent: 0, fontSize: 0 };

  probeCtx.font = font;
  const m = probeCtx.measureText(FONT_METRICS_PROBE);
  const ascent = m.actualBoundingBoxAscent ?? 0;
  const descent = m.actualBoundingBoxDescent ?? 0;
  const result: FontMetrics = { ascent, descent, fontSize: ascent + descent };
  fontMetricsCache.set(font, result);
  return result;
};

/**
 * Y of the alphabetic baseline for the `i`-th visual line, mirroring Pixi
 * v8's `CanvasTextGenerator`:
 *   linePositionY = j * lineHeight + ascent + (lineHeight - fontSize) / 2
 * with `fontSize` here meaning the *measured* `ascent + descent`, NOT the
 * authored style.fontSize. `blockTop` lets the caller place the block —
 * pass `centerY - (lineHeightPx * lineCount) / 2` to match Pixi's
 * `anchor.y = 0.5; y = height/2` convention.
 */
export const computeLineBaselineY = (
  blockTop: number,
  lineIndex: number,
  lineHeightPx: number,
  metrics: FontMetrics,
): number => {
  const shift =
    lineHeightPx - metrics.fontSize < 0
      ? 0
      : (lineHeightPx - metrics.fontSize) / 2;
  return blockTop + lineIndex * lineHeightPx + metrics.ascent + shift;
};
